<template>
  <div class="tariff-features">
    <div class="tariff-features__header" :class="headerClass">
      <div class="tariff-features__title">
        {{ $t(isPremiumTariff ? "Tariffs.features.plan_premium" : "Tariffs.features.plan_basic") }}
      </div>
      <div class="tariff-features__subtitle">
        {{ $t(isPremiumTariff ? "Tariffs.features.plan_premium_desc" : "Tariffs.features.plan_basic_desc") }}
      </div>
    </div>

    <div class="tariff-features__body">
      <template v-if="available.length">
        <div class="tariff-features__section-title">
          {{ $t("Tariffs.features.available_features") }}
        </div>

        <div class="tariff-features__list">
          <div
              v-for="item in available"
              :key="'a-' + item.key"
              class="tf-item tf-item--available"
          >
            <div class="tf-item__icon">
              <img :src="item.iconGreen" alt="" class="tf-item__img" />
            </div>

            <div class="tf-item__content">
              <div class="tf-item__name">{{ $t(item.nameKey) }}</div>
              <div class="tf-item__desc">{{ $t(item.descKey) }}</div>
            </div>
          </div>
        </div>
      </template>

      <template v-if="notPurchased.length">
        <div
            class="tariff-features__section-title tariff-features__section-title--spaced"
        >
          {{ $t("Tariffs.features.not_purchased_features") }}
        </div>

        <div class="tariff-features__list">
          <div
              v-for="item in notPurchased"
              :key="'n-' + item.key"
              class="tf-item tf-item--disabled"
          >
            <div class="tf-item__icon">
              <img :src="item.iconGray" alt="" class="tf-item__img" />
            </div>

            <div class="tf-item__content">
              <div class="tf-item__name">{{ $t(item.nameKey) }}</div>
              <div class="tf-item__desc">{{ $t(item.descKey) }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from "@vue/composition-api";

import ChecklistGreen from "@/assets/tariff-icons/checklist-green.svg";
import ChecklistGray from "@/assets/tariff-icons/checklist-gray.svg";

import TestGreen from "@/assets/tariff-icons/test-green.svg";
import TestGray from "@/assets/tariff-icons/test-gray.svg";

import DocumentGreen from "@/assets/tariff-icons/document-green.svg";
import DocumentGray from "@/assets/tariff-icons/document-gray.svg";

import NotificationGreen from "@/assets/tariff-icons/notification-green.svg";
import NotificationGray from "@/assets/tariff-icons/notification-gray.svg";

import CompanyHealthGreen from "@/assets/tariff-icons/company-health-green.svg";
import CompanyHealthGray from "@/assets/tariff-icons/company-health-gray.svg";

import StatisticsGreen from "@/assets/tariff-icons/statistics-green.svg";
import StatisticsGray from "@/assets/tariff-icons/statistics-gray.svg";

import MaterialsGreen from "@/assets/tariff-icons/materials-green.svg";
import MaterialsGray from "@/assets/tariff-icons/materials-gray.svg";

import ChatGreen from "@/assets/tariff-icons/chat-green.svg";
import ChatGray from "@/assets/tariff-icons/chat-gray.svg";

import BoardGreen from "@/assets/tariff-icons/board-green.svg";
import BoardGray from "@/assets/tariff-icons/board-gray.svg";

import FaceIdGreen from "@/assets/tariff-icons/faceId-green.svg";
import FaceIdGray from "@/assets/tariff-icons/faceid-gray.svg";

import SalaryGreen from "@/assets/tariff-icons/salary-green.svg";
import SalaryGray from "@/assets/tariff-icons/salary-gray.svg";

import MrBeeGreen from "@/assets/tariff-icons/mrbee-green.svg";
import MrBeeGray from "@/assets/tariff-icons/mrbee-gray.svg";

export default defineComponent({
  name: "TariffFeaturesCard",
  props: {
    tariff: {
      type: Object,
      required: true
    },
    tariffPlanFeatures: {
      type: Object,
      default: () => ({})
    },
  },

  setup(props) {
    const ALL = [
      { key: "checklist", code: "checklist", iconGreen: ChecklistGreen, iconGray: ChecklistGray,
        nameKey: "Tariffs.features.checklist.title", descKey: "Tariffs.features.checklist.desc" },

      { key: "trello", code: "trello", iconGreen: BoardGreen, iconGray: BoardGray,
        nameKey: "Tariffs.features.trello.title", descKey: "Tariffs.features.trello.desc" },

      { key: "messenger", code: "messenger", iconGreen: ChatGreen, iconGray: ChatGray,
        nameKey: "Tariffs.features.messenger.title", descKey: "Tariffs.features.messenger.desc" },

      { key: "salary", code: "salary", iconGreen: SalaryGreen, iconGray: SalaryGray,
        nameKey: "Tariffs.features.salary.title", descKey: "Tariffs.features.salary.desc" },

      { key: "statistics", code: "statistics", iconGreen: StatisticsGreen, iconGray: StatisticsGray,
        nameKey: "Tariffs.features.statistics.title", descKey: "Tariffs.features.statistics.desc" },

      { key: "company_health", code: "company_health", iconGreen: CompanyHealthGreen, iconGray: CompanyHealthGray,
        nameKey: "Tariffs.features.company_health.title", descKey: "Tariffs.features.company_health.desc" },

      { key: "company_materials", code: "company_materials", iconGreen: MaterialsGreen, iconGray: MaterialsGray,
        nameKey: "Tariffs.features.company_materials.title", descKey: "Tariffs.features.company_materials.desc" },

      { key: "announcement", code: "announcement", iconGreen: NotificationGreen, iconGray: NotificationGray,
        nameKey: "Tariffs.features.announcement.title", descKey: "Tariffs.features.announcement.desc" },

      { key: "test", code: "test", iconGreen: TestGreen, iconGray: TestGray,
        nameKey: "Tariffs.features.test.title", descKey: "Tariffs.features.test.desc" },

      { key: "documents", code: "documents", iconGreen: DocumentGreen, iconGray: DocumentGray,
        nameKey: "Tariffs.features.documents.title", descKey: "Tariffs.features.documents.desc" },

      { key: "face_id", code: "face_id", iconGreen: FaceIdGreen, iconGray: FaceIdGray,
        nameKey: "Tariffs.features.face_id.title", descKey: "Tariffs.features.face_id.desc" },

      { key: "mrbee", code: "analyze", iconGreen: MrBeeGreen, iconGray: MrBeeGray,
        nameKey: "Tariffs.features.mrbee.title", descKey: "Tariffs.features.mrbee.desc" },
    ];

    const flags = computed(() => props.tariffPlanFeatures || {});
    const hasAllAccess = computed(() => isPremiumTariff.value === true);

    const isFaceIdVisible = computed(() => flags.value.face_id === true);

    const visibleFeatures = computed(() =>
        ALL.filter((item) => item.code !== "face_id" || isFaceIdVisible.value),
    );

    const isEnabled = (item) => {
      if (hasAllAccess.value) return true;
      return flags.value[item.code] === true;
    };

    const available = computed(() => visibleFeatures.value.filter(isEnabled));
    const notPurchased = computed(() => visibleFeatures.value.filter((x) => !isEnabled(x)));

    const isPremiumTariff = computed(() => (props.tariff.plan_type === 2));

    const headerClass = computed(() =>
        isPremiumTariff.value
            ? "tariff-features__header--premium"
            : "tariff-features__header--basic"
    );

    return { available, notPurchased, headerClass, isPremiumTariff };
  },
});
</script>

<style lang="scss" scoped>
.tariff-features {
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(237, 240, 242, 1);
  max-width: 764px;
  width: 100%;
}

.tariff-features__header {
  padding: 16px 18px;
  text-align: center;

  &--premium {
    background: linear-gradient(
            180deg,
            #fffa82 0%,
            #e9ab1c 0.01%,
            #f9e15b 54.17%,
            #e9ab1c 100%
    );
  }

  &--basic {
    background: rgba(227, 230, 232, 1);
  }
}

.tariff-features__title {
  font-family: "Lato", sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 1;
  color: #384046;
}

.tariff-features__subtitle {
  margin-top: 8px;
  font-family: "Lato", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 1;
  color: #384046;
  max-width: 520px;
  margin: 8px auto 0;
}

.tariff-features__body {
  padding: 24px 32px;
}

.tariff-features__section-title {
  font-family: "Lato", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1;
  margin-bottom: 10px;
  color: rgba(56, 64, 70, 1);

  &--spaced {
    margin-top: 16px;
  }
}

.tariff-features__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tf-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid transparent;
  align-items: center;
}

.tf-item__icon {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
}

.tf-item__img {
  width: 24px;
  height: 24px;
  display: block;
}

.tf-item__name {
  font-weight: 700;
  font-size: 14px;
  color: #384046;
}

.tf-item__desc {
  margin-top: 4px;
  font-size: 14px;
  font-weight: 400;
  color: #384046;
}

.tf-item--available {
  border-color: rgba(223, 242, 232, 1);
}

.tf-item--disabled {
  border-color: rgba(212, 212, 212, 1);
  opacity: 0.85;
}
</style>